import { ref } from 'vue'

/**
 * Estado global de "cambios sin guardar" del panel admin.
 * Un singleton a nivel módulo (como siteContactPublicRef) para que las páginas
 * admin y el guard de rutas compartan el mismo estado reactivo.
 *
 * Uso en una página admin:
 *   - `registerSave(fn)` → guarda la función que persiste los cambios.
 *   - `markDirty()` → al editar algo (o vía watch deep sobre el form).
 *   - `markClean()` → tras cargar los datos y tras guardar con éxito.
 *
 * El guard de rutas (router.beforeEach) llama `blockNavigationIfDirty(to)`:
 * si hay cambios sin guardar, cancela la navegación y abre el modal.
 * El modal resuelve con `saveThenLeave()`, `leaveWithoutSaving()` o `cancelLeave()`.
 */
const isDirty = ref(false)
const showModal = ref(false)
let saveHandler = null
let pendingTarget = null
let dirtyStateGetter = null
let cleanStateSnapshot = null

function snapshotState() {
  if (typeof dirtyStateGetter !== 'function') return null
  try {
    return JSON.stringify(dirtyStateGetter())
  } catch {
    return null
  }
}

function hasActualChanges() {
  if (!isDirty.value) return false
  if (typeof dirtyStateGetter !== 'function') return true
  const currentSnapshot = snapshotState()
  return currentSnapshot === null || currentSnapshot !== cleanStateSnapshot
}

export function markDirty() {
  isDirty.value = typeof dirtyStateGetter === 'function'
    ? snapshotState() !== cleanStateSnapshot
    : true
}

export function markClean() {
  isDirty.value = false
  cleanStateSnapshot = snapshotState()
}

export function registerSave(fn) {
  saveHandler = fn
}

/** Registra el estado editable que debe compararse con la última carga/guardado. */
export function registerDirtyState(getter) {
  // Cada página registra después su propio guardado; evita conservar el handler
  // de una sección anterior al navegar sin cambios pendientes.
  saveHandler = null
  dirtyStateGetter = typeof getter === 'function' ? getter : null
  cleanStateSnapshot = snapshotState()
  isDirty.value = false
}

export function hasSaveHandler() {
  return typeof saveHandler === 'function'
}

/** Devuelve true si hay cambios sin guardar; en ese caso bloquea y abre el modal. */
export function blockNavigationIfDirty(to) {
  if (!hasActualChanges()) {
    isDirty.value = false
    return false
  }
  pendingTarget = to
  showModal.value = true
  return true
}

export function cancelLeave() {
  showModal.value = false
  pendingTarget = null
}

/** Sale descartando los cambios. Devuelve el destino pendiente (para re-navegar). */
export function leaveWithoutSaving() {
  isDirty.value = false
  saveHandler = null
  dirtyStateGetter = null
  cleanStateSnapshot = null
  showModal.value = false
  const target = pendingTarget
  pendingTarget = null
  return target
}

/** Guarda y sale. Devuelve el destino pendiente (para re-navegar). */
export async function saveThenLeave() {
  if (saveHandler) {
    const saved = await saveHandler()
    if (saved === false) return null
  }
  isDirty.value = false
  saveHandler = null
  dirtyStateGetter = null
  cleanStateSnapshot = null
  showModal.value = false
  const target = pendingTarget
  pendingTarget = null
  return target
}

/** Aviso nativo del navegador al cerrar/refrescar con cambios sin guardar. */
let beforeUnloadRegistered = false
export function initUnsavedChangesGuard() {
  if (beforeUnloadRegistered || typeof window === 'undefined') return
  beforeUnloadRegistered = true
  window.addEventListener('beforeunload', (e) => {
    if (hasActualChanges()) {
      e.preventDefault()
      // Valores legacy requeridos por algunos navegadores para mostrar el aviso.
      e.returnValue = ''
      return ''
    }
  })
}

export { isDirty, showModal }
